import { useState, useEffect, useRef, useCallback } from "react";
import Navbar from "./components/Navbar";
import PostFood from "./components/PostFood";
import FoodList from "./components/FoodList";
import FoodMap from "./components/FoodMap";
import SidePanel from "./components/SidePanel";
import ClaimModal from "./components/ClaimModal";
import DeliveryList from "./components/DeliveryList";
import SettingsModal from "./components/SettingsModal";
import ActiveDeliveryPopup from "./components/ActiveDeliveryPopup";
import ToastNotification from "./components/ToastNotification";
import SproutChat from "./components/SproutChat";
import Auth from "./components/Auth";
import KudosModal from "./components/KudosModal";
import useLocation from "./hooks/useLocation";
import { reverseGeocode } from "./utils/geocode";
import { auth, db } from "./firebase";
import { onAuthStateChanged } from "firebase/auth";
import { collection, onSnapshot, getDocs, updateDoc, doc, getDoc, setDoc, addDoc, deleteField, arrayUnion } from "firebase/firestore";

function App() {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState("donor");
  const [foods, setFoods] = useState([]);
  const [showLanding, setShowLanding] = useState(true);
  const [panelOpen, setPanelOpen] = useState(false);
  const [claimModalItem, setClaimModalItem] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [claimsOpen, setClaimsOpen] = useState(false);
  const [sproutOpen, setSproutOpen] = useState(false);
  const [showSproutGreeting, setShowSproutGreeting] = useState(true);
  const [homeLocation, setHomeLocation] = useState(null);
  const [toasts, setToasts] = useState([]);
  const [kudosModalItem, setKudosModalItem] = useState(null);
  const [kudosPromptEnabled, setKudosPromptEnabled] = useState(true);
  const [isAutoKudosPrompt, setIsAutoKudosPrompt] = useState(false);
  const [mapPickMode, setMapPickMode] = useState(null);
  const [prefilledDelivery, setPrefilledDelivery] = useState(null);
  const [postMapPickResult, setPostMapPickResult] = useState(null);
  const [searchFlyTarget, setSearchFlyTarget] = useState(null);
  const [searchCooldownUntil, setSearchCooldownUntil] = useState(0);
  const [tutorialCompleted, setTutorialCompleted] = useState(null);
  const prevFoodsRef = useRef([]);
  const { location: browserLocation } = useLocation();

  const location = browserLocation || homeLocation;

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      if (currentUser) setShowLanding(false);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!user) { setHomeLocation(null); return; }
    const load = async () => {
      try {
        const snap = await getDoc(doc(db, "user_settings", user.uid));
        if (snap.exists()) {
          const data = snap.data();
          if (data.homeLat) {
            setHomeLocation({ lat: data.homeLat, lng: data.homeLng, address: data.homeAddress || "" });
          }
          if (data.kudosPromptEnabled === false) setKudosPromptEnabled(false);
          setTutorialCompleted(data.tutorialCompleted === true);
        } else {
          setTutorialCompleted(false);
        }
      } catch (e) { console.error(e); }
    };
    load();
  }, [user]);

  useEffect(() => {
    if (user && tutorialCompleted === false) {
      setSproutOpen(true);
      setShowSproutGreeting(false);
    }
  }, [user, tutorialCompleted]);

  const handleSaveHome = useCallback(async ({ address, lat, lng }) => {
    setHomeLocation({ lat, lng, address });
    if (user) await setDoc(doc(db, "user_settings", user.uid), { homeLat: lat, homeLng: lng, homeAddress: address }, { merge: true });
  }, [user]);

  const handleClearHome = useCallback(async () => {
    setHomeLocation(null);
    if (user) await setDoc(doc(db, "user_settings", user.uid), { homeLat: null, homeLng: null, homeAddress: null }, { merge: true });
  }, [user]);

  const handleRequestMapPickForDelivery = (claimQty) => {
    if (!claimModalItem) return;
    setMapPickMode({ for: "delivery", item: claimModalItem, claimQty: claimQty ?? (parseInt(claimModalItem.quantity, 10) || 1) });
    setClaimModalItem(null);
  };

  const handleRequestMapPickForHome = () => {
    setSettingsOpen(false);
    setMapPickMode({ for: "home" });
  };

  const handleRequestMapPickForPost = () => {
    if (typeof window !== "undefined" && window.innerWidth < 768) setPanelOpen(false);
    setMapPickMode({ for: "post" });
  };

  const handleMapClick = useCallback(async ({ lat, lng }) => {
    if (!mapPickMode) return;
    const address = await reverseGeocode(lat, lng);
    if (mapPickMode.for === "home") {
      handleSaveHome({ address, lat, lng });
      setMapPickMode(null);
      setToasts(t => [...t, { id: `home_${Date.now()}`, type: "success", icon: "📍", title: "Home location set!", message: address }]);
    } else if (mapPickMode.for === "delivery") {
      const { item, claimQty } = mapPickMode;
      setPrefilledDelivery({ address, lat, lng, claimQty });
      setClaimModalItem(item);
      setMapPickMode(null);
    } else if (mapPickMode.for === "post") {
      setPostMapPickResult({ address, lat, lng });
      setMapPickMode(null);
      setToasts(t => [...t, { id: `post_${Date.now()}`, type: "success", icon: "📍", title: "Pickup location set!", message: address }]);
      if (typeof window !== "undefined" && window.innerWidth < 768) setPanelOpen(true);
    }
  }, [mapPickMode, handleSaveHome]);

  // Real-time listener: new pins show up immediately when added in Firestore
  useEffect(() => {
    if (!user) return;
    const unsub = onSnapshot(collection(db, "food_posts"), (snapshot) => {
      setFoods(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [user]);

  // Poll every 5 seconds as fallback so we always pick up new pins even if listener lags
  const POLL_INTERVAL_MS = 5000;
  useEffect(() => {
    if (!user) return;
    const poll = async () => {
      try {
        const snapshot = await getDocs(collection(db, "food_posts"));
        setFoods(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (e) {
        console.warn("Food poll failed:", e);
      }
    };
    poll();
    const id = setInterval(poll, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [user]);

  const dismissToast = useCallback((id) => setToasts(prev => prev.filter(t => t.id !== id)), []);

  const promptKudosIfEnabled = useCallback((item, recipientType = "donor") => {
    if (kudosPromptEnabled) {
      setIsAutoKudosPrompt(true);
      setKudosModalItem({ item, recipientType: recipientType === "both" && item.claimType === "delivery" ? "both" : "donor" });
    }
  }, [kudosPromptEnabled]);

  useEffect(() => {
    if (!user || prevFoodsRef.current.length === 0) { prevFoodsRef.current = foods; return; }
    const prev = prevFoodsRef.current;
    foods.forEach(f => {
      if (f.claimedBy !== user.uid || f.claimType !== "delivery") return;
      if (f.courierId === user.uid) return; // Only prompt the recipient (receiver), never the courier
      const old = prev.find(p => p.id === f.id);
      if (!old || old.deliveryStatus === f.deliveryStatus) return;
      const id = `${f.id}_${f.deliveryStatus}_${Date.now()}`;
      if (f.deliveryStatus === "accepted") setToasts(t => [...t, { id, type: "info", icon: "✅", title: "Courier accepted!", message: `A courier is heading to pick up "${f.name}"` }]);
      else if (f.deliveryStatus === "in_transit") setToasts(t => [...t, { id, type: "purple", icon: "🚴", title: "On the way!", message: `"${f.name}" is in transit to you` }]);
      else if (f.deliveryStatus === "delivered") {
        setToasts(t => [...t, { id, type: "success", icon: "🎉", title: "Delivered!", message: `"${f.name}" has been delivered` }]);
        const needsKudos = !f.kudosSent || (f.courierId && !f.courierKudosSent);
        if (needsKudos) promptKudosIfEnabled(f, "both");
      }
    });
    prevFoodsRef.current = foods;
  }, [foods, user, promptKudosIfEnabled]);

  const handleCancelClaim = async (id, role) => {
    if (role === "courier") {
      await updateDoc(doc(db, "food_posts", id), { deliveryStatus: "pending_delivery", courierId: deleteField() });
    } else {
      await updateDoc(doc(db, "food_posts", id), { claimed: false, claimType: deleteField(), claimedBy: deleteField(), deliveryStatus: deleteField(), deliveryAddress: deleteField(), deliveryLat: deleteField(), deliveryLng: deleteField(), courierId: deleteField() });
    }
  };

  const handlePickup = async (id, claimQty) => {
    const original = foods.find(f => f.id === id);
    if (!original) return;
    const totalQty = parseInt(original.quantity, 10) || 1;
    const qty = Math.min(Math.max(1, claimQty || totalQty), totalQty);
    if (qty < totalQty) {
      await updateDoc(doc(db, "food_posts", id), { quantity: totalQty - qty });
      const { id: _o, ...rest } = original;
      await addDoc(collection(db, "food_posts"), { ...rest, quantity: qty, claimed: true, claimType: "pickup", claimedBy: user.uid });
    } else {
      await updateDoc(doc(db, "food_posts", id), { claimed: true, claimType: "pickup", claimedBy: user.uid });
    }
    setClaimModalItem(null);
  };

  const handleDelivery = async (id, deliveryInfo) => {
    const original = foods.find(f => f.id === id);
    if (!original) return;
    const totalQty = parseInt(original.quantity, 10) || 1;
    const qty = Math.min(Math.max(1, deliveryInfo?.claimQty || totalQty), totalQty);
    const claimFields = {
      claimed: true, claimType: "delivery", claimedBy: user.uid, deliveryStatus: "pending_delivery",
      deliveryAddress: deliveryInfo?.deliveryAddress || "", deliveryLat: deliveryInfo?.deliveryLat || null, deliveryLng: deliveryInfo?.deliveryLng || null
    };
    if (qty < totalQty) {
      await updateDoc(doc(db, "food_posts", id), { quantity: totalQty - qty });
      const { id: _o, ...rest } = original;
      await addDoc(collection(db, "food_posts"), { ...rest, quantity: qty, ...claimFields });
    } else {
      await updateDoc(doc(db, "food_posts", id), claimFields);
    }
    setClaimModalItem(null);
  };

  const handleSendKudos = async (postId, payload, recipientType = "donor") => {
    try {
      if (recipientType === "both") {
        const item = foods.find(f => f.id === postId);
        const updates = {};
        if (payload.donor) {
          updates.kudos = arrayUnion({ fromUid: user.uid, emoji: payload.donor.emoji, message: payload.donor.message || "", timestamp: Date.now() });
          updates.kudosSent = true;
        }
        if (payload.courier && item?.courierId) {
          updates.courierKudos = arrayUnion({ fromUid: user.uid, toUid: item.courierId, emoji: payload.courier.emoji, message: payload.courier.message || "", timestamp: Date.now() });
          updates.courierKudosSent = true;
        }
        if (Object.keys(updates).length > 0) {
          await updateDoc(doc(db, "food_posts", postId), updates);
          setToasts(t => [...t, { id: `kudos_${Date.now()}`, type: "success", icon: "🙏", title: "Kudos sent!", message: "Your thanks have been shared" }]);
        }
      } else if (recipientType === "courier") {
        const item = foods.find(f => f.id === postId);
        if (!item?.courierId) return;
        const { emoji, message } = payload;
        await updateDoc(doc(db, "food_posts", postId), {
          courierKudos: arrayUnion({ fromUid: user.uid, toUid: item.courierId, emoji, message: message || "", timestamp: Date.now() }),
          courierKudosSent: true
        });
        setToasts(t => [...t, { id: `kudos_${Date.now()}`, type: "success", icon: "🙏", title: "Kudos sent!", message: "The courier will see your appreciation" }]);
      } else {
        const { emoji, message } = payload;
        await updateDoc(doc(db, "food_posts", postId), {
          kudos: arrayUnion({ fromUid: user.uid, emoji, message: message || "", timestamp: Date.now() }),
          kudosSent: true
        });
        setToasts(t => [...t, { id: `kudos_${Date.now()}`, type: "success", icon: "🙏", title: "Kudos sent!", message: "The donor will see your appreciation" }]);
      }
    } catch (err) {
      console.error(err);
      setToasts(t => [...t, { id: `kudos_err_${Date.now()}`, type: "error", icon: "❌", title: "Oops", message: "Could not send kudos. Try again!" }]);
    }
  };

  const handleSetKudosPrompt = async (enabled) => {
    setKudosPromptEnabled(enabled);
    if (user) await setDoc(doc(db, "user_settings", user.uid), { kudosPromptEnabled: enabled }, { merge: true });
  };

  const myActiveClaims = [
    ...foods.filter(f => f.claimedBy === user?.uid && f.claimType === "pickup" && !f.completed).map(f => ({ ...f, _role: "receiver" })),
    ...foods.filter(f => f.claimedBy === user?.uid && f.claimType === "delivery" && (f.deliveryStatus !== "delivered" || !f.receiverConfirmed)).map(f => ({ ...f, _role: "receiver" })),
    ...foods.filter(f => f.courierId === user?.uid && f.claimType === "delivery" && ["accepted", "in_transit"].includes(f.deliveryStatus)).map(f => ({ ...f, _role: "courier" }))
  ];

  const panelTitle = role === "donor" ? "Post Surplus Food" : role === "receiver" ? "Available Food" : "Deliveries";
  const panelButtonLabel = role === "donor" ? "📦 Post Food" : role === "receiver" ? "🔍 Browse Food" : "🚴 Deliveries";

  if (showLanding) {
    return (
      <div className="relative min-h-screen overflow-hidden flex items-center justify-center">
        <video autoPlay loop muted playsInline className="absolute inset-0 w-full h-full object-cover" ref={el => { if (el) el.playbackRate = 0.8; }}>
          <source src="/bg-video-60fps.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-black/40"></div>
        <div className="relative z-10 bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl px-10 py-12 md:px-16 md:py-16 max-w-xl mx-4 text-center border border-white/50">
          <h1 className="font-condensed text-7xl md:text-8xl font-extrabold text-green-600 mb-3 tracking-tight">Leafsy</h1>
          <p className="text-lg md:text-xl text-gray-600 mb-1 font-medium">We make it Leafsy to share — easy to use, easy on the earth.</p>
          <p className="text-sm text-gray-400 mb-10">Connect surplus food with people who need it.</p>
          <button onClick={() => setShowLanding(false)} className="btn-pop font-condensed px-12 py-4 bg-green-500 text-white text-lg font-bold uppercase rounded-full shadow-lg hover:bg-green-600">
            Get Started
          </button>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-100 p-6 flex flex-col items-center justify-center">
        <div className="flex items-center justify-center gap-3 mb-6">
          <img src="/image192.png" alt="Leafsy" className="w-14 h-14" />
          <h1 className="font-condensed text-5xl font-extrabold text-green-600 tracking-tight">Leafsy</h1>
        </div>
        <Auth user={user} setUser={setUser} />
      </div>
    );
  }

  return (
    <div className="h-screen w-screen overflow-hidden relative">
      <div className="absolute inset-0 z-0">
        <FoodMap location={location} foods={foods} onClaim={item => setClaimModalItem(item)} userId={user.uid} activeDeliveries={myActiveClaims.filter(c => c._role === "courier")} mapPickMode={mapPickMode} onMapClick={handleMapClick} searchFlyTarget={searchFlyTarget} onSearchFlown={() => setSearchFlyTarget(null)} searchCooldownUntil={searchCooldownUntil} />
      </div>

      {mapPickMode && (
        <div className="fixed top-[91px] left-1/2 -translate-x-1/2 z-[1002] px-6 py-3 bg-amber-400 text-gray-900 rounded-2xl shadow-lg font-semibold text-sm flex items-center gap-2">
          <span>📍</span> Click on the map to set {mapPickMode.for === "home" ? "home location" : mapPickMode.for === "post" ? "pickup location" : "delivery address"}
          <button onClick={() => setMapPickMode(null)} className="ml-2 w-6 h-6 rounded-full bg-black/10 hover:bg-black/20 flex items-center justify-center text-xs">✕</button>
        </div>
      )}

      <Navbar onRoleChange={setRole} user={user} floating={true} onOpenSettings={() => setSettingsOpen(true)} onSearchSelect={({ lat, lng }) => { setSearchFlyTarget({ lat, lng }); setSearchCooldownUntil(Date.now() + 10000); }} />

      {/* Sprout — bottom left circle + first-time greeting; on mobile bubble is above button so it doesn't cover Post Food / My Claims */}
      {!sproutOpen && user && (
        <div className="fixed z-[1001] flex flex-col md:flex-row items-start md:items-end gap-2 bottom-[max(1.5rem,env(safe-area-inset-bottom))] left-[max(1rem,env(safe-area-inset-left))] md:bottom-6 md:left-6">
          {showSproutGreeting && (
            <div
              className="glass-hover relative bg-white/95 backdrop-blur-xl shadow-lg rounded-2xl rounded-bl-md px-4 py-2.5 max-w-[200px] border border-gray-200/60 animate-bounce-once cursor-pointer order-1 md:order-2"
              onClick={() => { setClaimsOpen(false); setPanelOpen(false); setSproutOpen(true); setShowSproutGreeting(false); }}
            >
              <p className="text-sm text-gray-700 font-medium">Hey! I'm <span className="text-green-600 font-bold">Sprout</span> — tap me if you need any help! 🌱</p>
              <button
                onClick={(e) => { e.stopPropagation(); setShowSproutGreeting(false); }}
                className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-gray-200 hover:bg-gray-300 rounded-full flex items-center justify-center text-gray-500 text-[10px] transition"
              >
                ✕
              </button>
            </div>
          )}
          <button
            onClick={() => { setClaimsOpen(false); setPanelOpen(false); setSproutOpen(true); setShowSproutGreeting(false); }}
            className="w-14 h-14 rounded-full bg-green-100 shadow-lg flex items-center justify-center hover:scale-110 hover:shadow-xl transition-all duration-200 overflow-hidden border-2 border-green-300 flex-shrink-0 order-2 md:order-1"
            title="Ask Sprout"
          >
            <img src="/sprout-icon.png" alt="Sprout" className="w-10 h-10 object-contain" />
          </button>
        </div>
      )}

      {!claimsOpen && (
        <button onClick={() => { setPanelOpen(false); setSproutOpen(false); setClaimsOpen(true); }} className="glass-hover btn-pop fixed z-[1001] bg-white/85 backdrop-blur-xl shadow-lg px-5 py-3 rounded-full text-sm font-semibold text-gray-700 border border-white/40
          bottom-24 right-4 left-auto translate-x-0 md:bottom-auto md:top-[91px] md:left-4 md:right-auto md:translate-x-0">
          📋 My Claims{myActiveClaims.length > 0 && ` (${myActiveClaims.length})`}
        </button>
      )}
      {!panelOpen && (
        <button onClick={() => { setClaimsOpen(false); setSproutOpen(false); setPanelOpen(true); }} className="glass-hover btn-pop fixed z-[1001] bg-white/85 backdrop-blur-xl shadow-lg px-5 py-3 rounded-full text-sm font-semibold text-gray-700 border border-white/40
          bottom-[max(1.5rem,env(safe-area-inset-bottom))] right-[max(1rem,env(safe-area-inset-right))] left-auto translate-x-0 md:bottom-auto md:top-[91px] md:right-4 md:left-auto md:translate-x-0">
          {panelButtonLabel}
        </button>
      )}

      <SidePanel title={panelTitle} isOpen={panelOpen} onToggle={() => setPanelOpen(!panelOpen)}>
        {role === "donor" && <PostFood user={user} location={location} onRequestMapPick={handleRequestMapPickForPost} mapPickResult={postMapPickResult} onConsumedMapPickResult={() => setPostMapPickResult(null)} />}
        {role === "receiver" && <FoodList user={user} location={location} homeLocation={homeLocation} onClaim={item => setClaimModalItem(item)} foods={foods} />}
        {role === "deliver" && <DeliveryList user={user} location={location} foods={foods} />}
      </SidePanel>

      <ClaimModal item={claimModalItem} onPickup={handlePickup} onDelivery={handleDelivery} onClose={() => { setClaimModalItem(null); setPrefilledDelivery(null); }} homeLocation={homeLocation} prefilledDelivery={prefilledDelivery} onRequestMapPick={handleRequestMapPickForDelivery} />

      <ToastNotification toasts={toasts} onDismiss={dismissToast} />

      <ActiveDeliveryPopup claims={myActiveClaims} isOpen={claimsOpen} onToggle={() => setClaimsOpen(!claimsOpen)} onCancelClaim={handleCancelClaim} onSendKudos={item => { setKudosModalItem({ item, recipientType: item.claimType === "delivery" ? "both" : "donor" }); setIsAutoKudosPrompt(false); }} onMarkReceived={async (item) => { await updateDoc(doc(db, "food_posts", item.id), { receiverConfirmed: true }); const needsKudos = !item.kudosSent || (item.courierId && !item.courierKudosSent); if (needsKudos && kudosPromptEnabled) { setIsAutoKudosPrompt(true); setKudosModalItem({ item, recipientType: "both" }); } }} onMarkDone={promptKudosIfEnabled} />

      <KudosModal item={kudosModalItem?.item} recipientType={kudosModalItem?.recipientType} isOpen={!!kudosModalItem} onClose={() => { setKudosModalItem(null); setIsAutoKudosPrompt(false); }} onSend={handleSendKudos} showDontAskAgain={isAutoKudosPrompt} onDisablePrompt={handleSetKudosPrompt} />

      <SettingsModal isOpen={settingsOpen} onClose={() => setSettingsOpen(false)} homeLocation={homeLocation} onSaveHome={handleSaveHome} onClearHome={handleClearHome} browserLocation={browserLocation} userEmail={user?.email || user?.displayName} kudosPromptEnabled={kudosPromptEnabled} onToggleKudosPrompt={handleSetKudosPrompt} onRequestMapPick={handleRequestMapPickForHome} onLogout={async () => { const { signOut } = await import("firebase/auth"); await signOut(auth); setUser(null); setSettingsOpen(false); setShowLanding(true); }} />

      <SproutChat isOpen={sproutOpen} onClose={() => setSproutOpen(false)} tutorialMode={tutorialCompleted === false} onTutorialComplete={async () => { setTutorialCompleted(true); if (user) await setDoc(doc(db, "user_settings", user.uid), { tutorialCompleted: true }, { merge: true }); setSproutOpen(false); }} />
    </div>
  );
}

export default App;
